import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Target, Users, TrendingUp, Handshake, Building2 } from 'lucide-react';

export default function About() {
  const { t } = useLanguage();

  const values = [
    { icon: <Target className="w-6 h-6 text-goszakup-600" />, title: t('nav.tracker'), desc: t('landing.feat1Desc') },
    { icon: <TrendingUp className="w-6 h-6 text-goszakup-600" />, title: t('nav.calculator'), desc: t('calc.subtitle') },
    { icon: <Users className="w-6 h-6 text-goszakup-600" />, title: t('landing.title'), desc: t('landing.subtitle') },
  ];

  const partners = [
    "D&J Emporio",
    "Qazaqstan Expert",
    "СТРОЙКОМПЛЕКТ",
    "ALKHARU GROUP",
    "PremierExpert",
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
        @keyframes slideRight {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        .animate-slide-right {
          animation: slideRight 25s linear infinite;
        }
      `}</style>

      {/* ── Hero ─────────────────────────────────── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-10 md:py-20 text-center">
          <span className="inline-flex items-center gap-1.5 bg-goszakup-50 text-goszakup-700 text-xs font-bold px-3 py-1 rounded-full border border-goszakup-200 mb-4 uppercase tracking-wider">
            G-Launcher
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-goszakup-700 to-goszakup-500 mb-4 md:mb-6 leading-tight tracking-tight">
            {t('about.title')}
          </h1>
          <p className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed">
            {t('about.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 md:py-16 space-y-8 md:space-y-16">

        {/* ── About block ──────────────────────────── */}
        <div className="bg-white rounded-2xl md:rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Mobile: text only */}
          <div className="p-5 md:p-10 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
              <div className="space-y-4 text-sm sm:text-base md:text-lg text-gray-700">
                <p className="leading-relaxed border-l-4 border-goszakup-500 pl-4 text-gray-800 font-medium">
                  {t('about.desc1')}
                </p>
                <p className="leading-relaxed text-gray-600">
                  {t('about.desc2')}
                </p>
              </div>
              {/* Decorative — hidden on mobile */}
              <div className="hidden lg:flex relative h-72 rounded-2xl overflow-hidden bg-goszakup-50 border border-goszakup-100 items-center justify-center group hover:bg-goszakup-100 transition-colors cursor-default">
                <div className="absolute inset-0 bg-gradient-to-br from-goszakup-200/50 to-transparent"></div>
                <Handshake className="w-32 h-32 text-goszakup-400 group-hover:scale-110 transition-transform duration-500 opacity-60" />
              </div>
            </div>
          </div>
        </div>

        {/* ── Features ─────────────────────────────── */}
        <div>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 mb-5 md:mb-10 text-center tracking-tight">
            {t('landing.featuresTitle')}
          </h2>

          {/* Mobile: compact horizontal cards */}
          <div className="flex flex-col gap-3 md:hidden">
            {values.map((v, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-start gap-4">
                <div className="w-10 h-10 bg-goszakup-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  {v.icon}
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">{v.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-6 md:gap-8">
            {values.map((v, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:-translate-y-2 hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 bg-goszakup-50 rounded-2xl flex items-center justify-center mb-5">
                  <div className="scale-125">{v.icon}</div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{v.title}</h3>
                <p className="text-gray-500 leading-relaxed text-base">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Partners ─────────────────────────────── */}
        <div>
          <h2 className="text-xs sm:text-sm font-bold text-center text-gray-400 mb-5 md:mb-8 uppercase tracking-[0.2em]">
            {t('about.partnersTitle')}
          </h2>
          <div className="relative w-full overflow-hidden bg-white rounded-2xl md:rounded-3xl py-5 md:py-8 border border-gray-100 shadow-sm flex items-center">
            <div className="absolute inset-y-0 left-0 w-10 md:w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
            <div className="absolute inset-y-0 right-0 w-10 md:w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
            <div className="flex w-max animate-slide-right items-center hover:[animation-play-state:paused] pr-[200px]">
              {[...partners, ...partners, ...partners].map((partner, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-5 md:px-10 text-gray-400 hover:text-goszakup-600 transition-colors duration-300 min-w-max group cursor-default"
                >
                  <div className="w-7 h-7 md:w-9 md:h-9 rounded-lg md:rounded-xl bg-gray-100 group-hover:bg-goszakup-50 flex items-center justify-center transition-colors flex-shrink-0">
                    <Building2 className="w-3.5 h-3.5 md:w-4 md:h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <span className="text-sm md:text-xl font-black tracking-tight opacity-70 group-hover:opacity-100 whitespace-nowrap">
                    {partner}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Mission ──────────────────────────────── */}
        <div className="bg-goszakup-600 rounded-2xl md:rounded-3xl p-6 sm:p-10 md:p-16 text-center text-white relative overflow-hidden shadow-xl">
          <div className="absolute top-0 right-0 -mt-6 -mr-6 w-24 h-24 md:w-40 md:h-40 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 -mb-6 -ml-6 w-24 h-24 md:w-40 md:h-40 bg-black/10 rounded-full blur-3xl"></div>
          <div className="relative z-10 w-12 h-12 md:w-20 md:h-20 mx-auto bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/30 mb-5 md:mb-8">
            <Target className="w-6 h-6 md:w-10 md:h-10 text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-3 md:mb-6 tracking-tight">
            {t('about.missionTitle')}
          </h2>
          <p className="text-lg sm:text-xl text-goszakup-100 max-w-2xl mx-auto leading-relaxed font-medium font-serif italic">
            "{t('about.missionText')}"
          </p>
        </div>

      </div>
    </div>
  );
}